import {
  ClassifierFormDataType,
  ClassifiersGroupsFormDataType,
} from './formData/types';
import {
  ClassifierFormDataSchema,
  ClassifierGroupsFormDataSchema,
} from './formData/ClassifierFormDataSchema';
import { ClassifierValidFormDataType } from './validFormData/types';
import { ClassifierMutationDtoSchema } from './mutationDto/ClassifierMutationDtoSchema';
import { ClassifierMutationDtoType } from './mutationDto/types';
import { ClassifierValidFormDataSchema } from './validFormData/ClassifierValidFormDataSchema';
import { ClassifierUploadSection } from '../query/ClassifierUploadSection';

export class ClassifierSchema {
  static parseFormData<Dto>(data: Dto): ClassifierFormDataType {
    return ClassifierFormDataSchema.parse(data);
  }

  static parseGroupsFormData<Dto>(data: Dto): ClassifiersGroupsFormDataType {
    return ClassifierGroupsFormDataSchema.parse(data);
  }

  static parseUploadFormData<Dto>(
    data: Dto,
    sections: ClassifierUploadSection[],
  ): Partial<ClassifierFormDataType> {
    // Test if the data can be parsed as complete form data first
    const parsedFormData = ClassifierFormDataSchema.safeParse(data);
    if (parsedFormData.success) {
      return parsedFormData.data;
    }

    const record = data as Record<string, unknown>;
    const hasGroupsData =
      'categories' in record ||
      'categoryGroups' in record ||
      'combinedGroups' in record;

    // Attempt to parse partial groups data
    if (hasGroupsData) {
      const parsedGroups = ClassifierGroupsFormDataSchema.parse({
        categories: record.categories ?? [],
        categoryGroups: record.categoryGroups ?? [],
        combinedGroups: record.combinedGroups ?? [],
      });

      const uploadData: Partial<ClassifierFormDataType> = {};

      if (sections.includes('CATEGORIES') || 'categories' in record) {
        uploadData.categories = parsedGroups.categories;
        uploadData.hierarchy = parsedGroups.hierarchy;
      }

      if (sections.includes('CATEGORY_GROUPS') || 'categoryGroups' in record) {
        uploadData.categoryGroups = parsedGroups.categoryGroups;
      }

      if (sections.includes('COMBINED_GROUPS') || 'combinedGroups' in record) {
        uploadData.combinedGroups = parsedGroups.combinedGroups;
      }

      return uploadData;
    }

    // If not only groups data, try to parse as a complete form data for diagnostics
    return ClassifierFormDataSchema.parse(data);
  }

  static parseMutationDto<
    ValidFormData extends Partial<ClassifierValidFormDataType>,
  >(validFormData: ValidFormData): ClassifierMutationDtoType {
    return ClassifierMutationDtoSchema.parse(validFormData);
  }

  static validFormDataSchema = ClassifierValidFormDataSchema;
}
